import { isDefined, isString } from '@allurion/utils';

import { getUserSessionToken } from 'src/helpers/userSession';
import { Logger } from 'src/services/Logger';
import env from 'src/utils/env';

export const { BASE_API_NEW, BASE_EXPORT_API, BASE_CONTENT_API } = env;

class Api {
  baseUrl: string;

  throwOnStatusCode = false;

  defaultHeaders: any;

  constructor(baseUrl: string, defaultHeaders: any = {}) {
    this.baseUrl = baseUrl;
    this.defaultHeaders = defaultHeaders;
  }

  async get<R>(url: string, opts: any = {}): Promise<R> {
    return this.httpRequest(url, 'GET', opts);
  }

  async post<R>(url: string, body: any = {}, opts: any = {}): Promise<R> {
    return this.httpRequestWithBody(url, 'POST', body, opts);
  }

  async patch<R>(url: string, body: any = {}, opts: any = {}): Promise<R> {
    return this.httpRequestWithBody(url, 'PATCH', body, opts);
  }

  async put<R>(url: string, body: any = {}, opts: any = {}): Promise<R> {
    return this.httpRequestWithBody(url, 'PUT', body, opts);
  }

  async delete<R>(url: string, body: any = {}, opts: any = {}): Promise<R> {
    return this.httpRequestWithBody(url, 'DELETE', body, opts);
  }

  async httpRequestWithBody<R>(
    url: string,
    method: string,
    body: any = {},
    opts: any = {}
  ): Promise<R> {
    return this.httpRequest(url, method, {
      ...opts,
      body: JSON.stringify(body),
    });
  }

  async httpRequest(
    url: string,
    method: string,
    { isFile = false, isPublic = false, ...options }: any = {}
  ) {
    const token = await getUserSessionToken();

    if (!token && !isPublic) {
      throw new Error('Unauthorized');
    }

    const withToken = token ? { Authorization: `Bearer ${token}` } : {};

    const requestInit = {
      ...options,
      headers: {
        Accept: '*/*',
        'Content-Type': 'application/json',
        ...this.defaultHeaders,
        ...options.headers,
        ...withToken,
      },
      method,
    };

    return fetch(`${this.baseUrl}${url}`, requestInit).then(async (res) => {
      // These two messages are matched by exact equality in
      // `ErrorBoundary.getErrorKind`, so they must stay verbatim. Naming the
      // endpoint here would send both to its `unknown` branch, which loses the
      // forbidden/unauthorized screens *and* starts reporting to Sentry every
      // 401/403 that reaches the boundary -- which the hooks using SWR
      // `suspense: true` do -- the 4xx-noise pattern that caused the July
      // error overage.
      // Giving these the endpoint too needs a typed error carrying `status`,
      // with ErrorBoundary switching on that instead of the string.
      if (res.status === 401) {
        throw new Error('Unauthorized');
      }

      if (res.status === 403) {
        throw new Error('Forbidden');
      }

      if (res.status < 400) {
        return isFile ? res.blob() : res.json();
      }

      // A failing response is not obliged to carry JSON. A gateway 502/504
      // answers with HTML, an empty body parses as nothing, and a file request
      // never asked for JSON in the first place. Parsing used to run before the
      // report below, so on those responses `res.json()` rejected and the 5xx
      // was never reported at all -- the failures carrying the least context
      // were exactly the ones that went unrecorded. Tolerate the unparseable
      // body here and fall through to the generic message.
      //
      // Only the failure path is tolerant: a success that does not parse is
      // still an error, and swallowing it would hand callers an empty body.
      const body = await res.json().catch(() => undefined);

      const serverMessage = (body as { error: string })?.error ?? 'Internal server error';
      // The thrown message stays exactly what it has always been, and Sentry
      // gets a separate error that names the endpoint -- the only place the
      // detail was missing.
      //
      // No consumer of *this* message was found, in either direction: nothing
      // matches it by string and nothing renders it. That is the reason for the
      // split rather than an argument against it. `Api` is the client for every
      // request the app makes, its rejections reach callers through SWR and
      // hand-rolled catches alike, and a search proves only where a consumer is
      // not -- so changing what all of them receive, for detail that is only
      // needed in Sentry, buys nothing and risks something. The 401/403 messages
      // above are different: `ErrorBoundary.getErrorKind` really does compare
      // them by equality.
      const error = new Error(serverMessage);

      if (res.status >= 500) {
        // 5xx only, deliberately. Reporting 4xx from a shared client is what
        // produced the July error overage in `vcs-admin-portal` -- bots probing
        // routes, ~55k events, none of them a defect -- and the free-tier quota
        // now drops real errors rather than billing for the noise.
        Logger.captureException(
          new Error(describeRequestFailure(method, url, res.status, serverMessage)),
          {
            // Without a fingerprint Sentry groups on the stack, and this
            // helper is the stack for every request the app makes: 2,388 events
            // across 2.5 years arrived as one issue titled "Internal Server
            // error", with no way to tell which endpoint failed. Grouping on
            // the *normalised* route keeps one issue per endpoint and status
            // while the reported message keeps the ids.
            fingerprint: ['api-request-failed', method, routeOf(url), String(res.status)],
          }
        );
      }

      throw error;
    });
  }
}

export const InsightsApi = new Api(BASE_API_NEW!);
export const InsightsExportApi = new Api(BASE_EXPORT_API!);
// throw an error if statusCode >= 400
InsightsApi.throwOnStatusCode = true;
export const ContentApi = new Api(BASE_CONTENT_API!);

/**
 * The message a failed request carries.
 *
 * Names the method, the path and the status, so the Sentry title identifies the
 * request instead of describing it.
 *
 * **The query string is dropped, and that is a privacy requirement rather than
 * tidiness.** Query values include free text a clinician typed: `search` in
 * `useClinicPatients` and `useProviderAccess` is the patients-search box
 * (`ManagePatientsPage`, straight from `e.target.value`), which is normally a
 * patient's name. Path segments are kept — they are ids, and Sentry already
 * stores the page URL containing the same ones — but user-entered text is a
 * different class of data and must not reach a third party in an error title.
 * The endpoint is what identifies the failure; no query value was ever needed
 * for that.
 *
 * Used only for what is reported, never for what is thrown: callers keep
 * receiving the server's own message, unchanged.
 */
export function describeRequestFailure(
  method: string,
  url: string,
  status: number,
  serverMessage: string
) {
  const [pathname] = url.split('?');

  return `${method} ${pathname} failed with ${status}: ${serverMessage}`;
}

/**
 * The path with its identifiers replaced, for grouping only.
 *
 * `/clinics/9886/patients/335365` and the same route for another patient are
 * one problem, not two, so the fingerprint has to ignore the ids and the query
 * string that the message keeps. Numeric and UUID segments become `:id`.
 */
export function routeOf(url: string) {
  const [pathname] = url.split('?');

  return pathname
    .split('/')
    .map((segment) =>
      /^\d+$/.test(segment) ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
        ? ':id'
        : segment
    )
    .join('/');
}

export function buildUrl(
  pathname: string,
  query?: { [attribute: string]: string | number | boolean | undefined }
) {
  let url = pathname;

  const queryStr = Object.entries(query ?? {})
    .filter(([, value]) => isDefined(value) && (!isString(value) || value !== ''))
    .map(([attribute, value]) => `${attribute}=${value}`)
    .join('&');

  if (queryStr) {
    url += `?${queryStr}`;
  }

  return url;
}
